interface ToolIndicatorProps {
    toolName: string;
    description: string;
    status?: 'error';
}

// Friendly labels for the tool indicator spinner. Keep in sync with the
// tool registrations in peerception-api/api/agent/tools/. Use a present
// participle ("Listing X", "Updating X") matching the existing style.
// Preview tools (*_preview) are never shown to the user — they're invoked
// internally by the orchestrator before write confirmations — so they do
// not need entries here.
const TOOL_LABELS: Record<string, string> = {
    // Read — models / scenarios / hypotheses
    list_models: 'Listing models',
    get_model_structure: 'Reading model structure',
    list_scenarios: 'Listing scenarios',
    get_scenario_details: 'Reading scenario details',
    get_hypothesis_details: 'Reading hypothesis details',
    get_output_block_details: 'Reading output details',
    get_field_data: 'Fetching field data',
    get_computation_results: 'Reading computation results',
    get_variable_dependencies: 'Checking dependencies',
    search_building_blocks: 'Searching building blocks',
    search_block_documentation: 'Searching documentation',
    get_field_options: 'Loading field options',
    get_scenario_errors: 'Checking for errors',
    get_field_comments: 'Reading field comments',

    // Read — charts
    get_chart_data: 'Fetching chart data',
    list_chartable_fields: 'Listing chartable fields',
    render_chart: 'Generating chart',

    // Read — comparison & audit
    compare_scenarios: 'Comparing scenarios',
    compare_scenario_sensitivity: 'Comparing sensitivity',
    compare_sensitivity_outputs: 'Comparing sensitivity outputs',
    run_audit: 'Running audit checks',
    get_audit_group_details: 'Inspecting audit group',

    // Read — library
    list_library_blocks: 'Listing library blocks',
    get_library_block_details: 'Reading library block details',

    // Read — portfolio
    list_portfolio_groups: 'Listing portfolio groups',
    get_portfolio_details: 'Reading portfolio details',
    get_portfolio_overview: 'Reading portfolio overview',
    get_portfolio_funding: 'Reading portfolio funding',
    get_portfolio_distributions: 'Reading portfolio distributions',
    get_portfolio_concentration: 'Reading portfolio concentration',
    get_portfolio_staleness: 'Checking portfolio staleness',
    list_available_companies_for_portfolio: 'Listing available companies',

    // Write — scenarios
    create_scenario: 'Creating scenario',
    duplicate_scenario: 'Duplicating scenario',
    rename_scenario: 'Renaming scenario',
    delete_scenario: 'Deleting scenario',
    update_hypothesis_value: 'Updating value',
    run_computation: 'Running computation',

    // Write — building blocks
    create_building_block: 'Creating building block',
    duplicate_building_block: 'Duplicating building block',
    rename_building_block: 'Renaming building block',
    delete_building_block: 'Deleting building block',

    // Write — timing & time series
    update_timing_reference: 'Selecting timing reference',
    update_time_serie_timing_reference: 'Switching time series timing',
    update_time_serie_timing: 'Updating timing dates',
    update_time_serie_elems: 'Updating time series data',

    // Write — list elements
    add_list_elem: 'Adding list element',
    update_list_elem: 'Updating list element',
    delete_list_elem: 'Removing list element',

    // Write — sensitivities
    create_sensitivity: 'Creating sensitivity',
    rename_sensitivity: 'Renaming sensitivity',
    delete_sensitivity: 'Deleting sensitivity',
    update_sensitivity_value: 'Updating sensitivity value',
    update_sensitivity_timing: 'Updating sensitivity timing',
    update_sensitivity_time_serie_elems: 'Updating sensitivity time series',
    add_sensitivity_list_elem: 'Adding sensitivity list element',
    update_sensitivity_list_elem: 'Updating sensitivity list element',
    delete_sensitivity_list_elem: 'Removing sensitivity list element',

    // Write — comments
    add_field_comment: 'Adding comment',
    update_field_comment: 'Updating comment',
    delete_field_comment: 'Deleting comment',

    // Write — library
    apply_library_block: 'Applying library block',
    save_to_library: 'Saving to library',
    delete_personal_library_block: 'Deleting library block',

    // Write — portfolio
    create_portfolio_group: 'Creating portfolio group',
    update_portfolio_group: 'Updating portfolio group',
    add_asset_to_portfolio: 'Adding asset to portfolio',
    update_portfolio_asset: 'Updating portfolio asset',
    remove_asset_from_portfolio: 'Removing asset from portfolio',
    create_portfolio_tag: 'Creating portfolio tag',
    delete_portfolio_tag: 'Deleting portfolio tag',
    compute_portfolio: 'Computing portfolio',
    refresh_portfolio_asset: 'Refreshing portfolio asset',
};

export default function ToolIndicator({ toolName, description, status }: ToolIndicatorProps) {
    const label = TOOL_LABELS[toolName] || description || `Running ${toolName}`;

    if (status === 'error') {
        return (
            <div className="flex items-center gap-2 text-xs text-amber-600 py-1">
                <svg className="h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
                <span>{label} failed</span>
            </div>
        );
    }

    return (
        <div className="flex items-center gap-2 text-xs text-slate-500 py-1">
            <svg className="animate-spin h-3 w-3 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <span>{label}...</span>
        </div>
    );
}
